<template>
  <div class="sci-view-container">
    <!-- Header -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button @click.prevent="home" class="sci-btn-back" title="Torna al Gestionale">
          <i class="fas fa-arrow-left"></i>
          <span>Gestionale</span>
        </button>
        <div class="sci-title-group">
          <div class="sci-badge-tag">
            <i class="fas fa-concierge-bell"></i> Sala & Comande
          </div>
          <h1 class="sci-view-title">{{ $t('chiamate_cameriere') || 'Chiamate Cameriere & Assistenza' }}</h1>
          <p class="sci-view-subtitle">Dashboard live per la ricezione e la gestione rapida delle chiamate dai tavoli</p>
        </div>
      </div>
      <div class="sci-header-actions">
        <div class="sci-live-pill">
          <span class="live-pulse-dot"></span>
          <span>Live Socket Attivo</span>
        </div>
        <button
          v-if="chiamateCameriere.length > 0"
          class="sci-btn sci-btn-danger-outline"
          @click="confirmEvadeAll"
          title="Evadi tutte le chiamate attive"
        >
          <i class="fas fa-check-double"></i>
          <span>Evadi Tutte ({{ chiamateCameriere.length }})</span>
        </button>
      </div>
    </header>

    <!-- Bento KPI Summary -->
    <section class="sci-kpi-grid">
      <div class="sci-kpi-card" :class="chiamateCameriere.length > 0 ? 'alert' : 'primary'">
        <div class="kpi-icon-wrap" :class="chiamateCameriere.length > 0 ? 'red' : 'green'">
          <i :class="chiamateCameriere.length > 0 ? 'fas fa-bell fa-shake' : 'fas fa-check-circle'"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Chiamate in Attesa</span>
          <span class="kpi-value" :class="{ 'text-danger': chiamateCameriere.length > 0 }">
            {{ chiamateCameriere.length }}
          </span>
          <span class="kpi-sub">
            {{ chiamateCameriere.length > 0 ? 'Richiesta assistenza dai tavoli' : 'Nessuna richiesta pendente' }}
          </span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap blue">
          <i class="fas fa-chair"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Ultima Chiamata Ricevuta</span>
          <span class="kpi-value text-normal">
            {{ latestCallText }}
          </span>
          <span class="kpi-sub">Aggiornamento automatico via WebSocket</span>
        </div>
      </div>
    </section>

    <!-- Main Calls Grid Section -->
    <section class="sci-card sci-calls-card">
      <div class="calls-card-header">
        <h3><i class="fas fa-bullhorn"></i> Richieste Attive in Sala</h3>
        <span class="header-hint"><i class="fas fa-hand-pointer"></i> Tocca una tessera tavolo per confermare l'evasione</span>
      </div>

      <div class="calls-grid-wrapper">
        <transition-group name="call-card" tag="div" class="calls-grid" v-if="chiamateCameriere.length > 0">
          <div
            v-for="call in chiamateCameriere"
            :key="call.sala + '_' + call.tavolo"
            class="call-tile"
            @click="confirmEvasion(call)"
          >
            <div class="call-tile-header">
              <span class="sala-pill">
                <i class="fas fa-map-marker-alt"></i> {{ call.sala || 'Sala' }}
              </span>
              <span class="pulse-beacon"></span>
            </div>

            <div class="call-tile-body">
              <div class="table-avatar">
                <span class="t-prefix">T</span>
                <span class="t-number">{{ normalizeId(call.tavolo) }}</span>
              </div>
            </div>

            <div class="call-tile-footer">
              <button class="sci-btn-evadi">
                <i class="fas fa-check"></i>
                <span>Tocca per Evadere</span>
              </button>
            </div>
          </div>
        </transition-group>

        <!-- Empty State -->
        <div v-else class="sci-state-feedback">
          <div class="empty-icon-wrap green">
            <i class="fas fa-bell-slash"></i>
          </div>
          <p class="state-title">{{ $t('nessuna_chiamata') || 'Nessuna chiamata attiva in sala' }}</p>
          <p class="state-subtitle">Le chiamate dai clienti o dal personale di sala appariranno qui istantaneamente con allerta visiva.</p>
        </div>
      </div>
    </section>

    <!-- Modal Evadi Singola Chiamata -->
    <div class="sci-modal-overlay" v-if="showEvadiModal" @click.self="showEvadiModal = false">
      <div class="sci-modal-dialog mini">
        <div class="sci-modal-header">
          <div class="modal-title-wrap">
            <span class="sci-badge-tag"><i class="fas fa-check-circle"></i> Evasione</span>
            <h3>{{ $t('evasione_chiamata') || 'Conferma Evasione' }}</h3>
          </div>
          <button @click="showEvadiModal = false" class="sci-modal-close">&times;</button>
        </div>
        <div class="sci-modal-body text-center">
          <div class="evade-preview">
            <span class="preview-badge">TAVOLO T{{ normalizeId(selectedCall.tavolo) }}</span>
            <span class="preview-sala">{{ selectedCall.sala }}</span>
          </div>
          <p class="evade-question">
            Confermi di aver preso in carico la richiesta per il tavolo <strong>T{{ normalizeId(selectedCall.tavolo) }}</strong>?
          </p>
          <div class="modal-actions-row">
            <button class="sci-btn sci-btn-secondary flex-1" @click="showEvadiModal = false">
              {{ $t('annulla') || 'Annulla' }}
            </button>
            <button class="sci-btn sci-btn-primary flex-1" @click="evadiChiamata">
              <i class="fas fa-check"></i> {{ $t('evadi') || 'Conferma Evasione' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { io } from "socket.io-client";
import { store } from '../store/store.js';

export default {
  name: 'ChiamateCameriere',
  data() {
    return {
      chiamateCameriere: [],
      socket: io(process.env.VUE_APP_SOCKET_URL, { autoConnect: true }),
      store,
      showEvadiModal: false,
      selectedCall: null
    };
  },
  computed: {
    latestCallText() {
      if (!this.chiamateCameriere.length) return 'Nessuna';
      const c = this.chiamateCameriere[0];
      return `T${this.normalizeId(c.tavolo)} (${c.sala})`;
    }
  },
  methods: {
    home() {
      this.$router.push({ name: 'Gestionale' });
    },
    normalizeId(id) {
      let tid = String(id || '');
      return tid.startsWith('t') ? tid.slice(1) : tid;
    },
    loadCalls() {
      try {
        const saved = localStorage.getItem('chiamateCameriere');
        if (saved) {
          this.chiamateCameriere = JSON.parse(saved);
        }
      } catch (e) {
        this.chiamateCameriere = [];
      }
    },
    confirmEvasion(call) {
      this.selectedCall = call;
      this.showEvadiModal = true;
    },
    confirmEvadeAll() {
      if (confirm(`Confermi l'evasione massiva di tutte le ${this.chiamateCameriere.length} chiamate?`)) {
        this.chiamateCameriere.forEach(call => {
          this.socket.emit('evasioneChiamata', { sala: call.sala, tavolo: call.tavolo });
        });
        this.chiamateCameriere = [];
        localStorage.setItem('chiamateCameriere', JSON.stringify([]));
      }
    },
    evadiChiamata() {
      if (!this.selectedCall) return;
      
      const { sala, tavolo } = this.selectedCall;
      
      // Rimuovi localmente
      this.chiamateCameriere = this.chiamateCameriere.filter(c => 
        !(c.sala === sala && c.tavolo === tavolo)
      );
      localStorage.setItem('chiamateCameriere', JSON.stringify(this.chiamateCameriere));
      
      // Invia evento socket
      this.socket.emit('evasioneChiamata', { sala, tavolo });
      
      this.showEvadiModal = false;
      this.selectedCall = null;
    }
  },
  mounted() {
    this.loadCalls();
    
    // Ascolta nuove chiamate
    this.socket.on('ricevutaChiamataCameriere', (messaggio) => {
      console.log("Socket: ricevutaChiamataCameriere", messaggio);
      if (!messaggio) return;
      const esisteGia = this.chiamateCameriere.some(c => 
        String(c.sala).trim().toLowerCase() === String(messaggio.sala).trim().toLowerCase() && 
        String(c.tavolo) === String(messaggio.tavolo)
      );

      if (!esisteGia) {
        this.chiamateCameriere.unshift({
          sala: messaggio.sala,
          tavolo: messaggio.tavolo
        });
        localStorage.setItem('chiamateCameriere', JSON.stringify(this.chiamateCameriere));
      }
    });

    // Ascolta evento alternativo "cameriere"
    this.socket.on('cameriere', (messaggio) => {
      console.log("Socket: cameriere", messaggio);
      if (!messaggio) return;
      const sala = messaggio.sala || 'N/A';
      const tavolo = messaggio.tavolo || messaggio.label || 'N/A';
      
      const esisteGia = this.chiamateCameriere.some(c => 
        c.sala === sala && c.tavolo === tavolo
      );
      if (!esisteGia) {
        this.chiamateCameriere.unshift({ sala, tavolo });
        localStorage.setItem('chiamateCameriere', JSON.stringify(this.chiamateCameriere));
      }
    });

    // Ascolta evasioni da altri terminali
    this.socket.on('ricevutaEvasioneChiamata', (messaggio) => {
      console.log("Socket: ricevutaEvasioneChiamata", messaggio);
      if (!messaggio) return;
      const targetSala = String(messaggio.sala || '').trim().toLowerCase();
      const targetTavolo = String(messaggio.tavolo);
      
      this.chiamateCameriere = this.chiamateCameriere.filter(c => {
        const sameSala = String(c.sala || '').trim().toLowerCase() === targetSala;
        const sameTavolo = String(c.tavolo) === targetTavolo;
        return !(sameSala && sameTavolo);
      });
      localStorage.setItem('chiamateCameriere', JSON.stringify(this.chiamateCameriere));
    });
  }
};
</script>

<style scoped>
.sci-view-container {
  min-height: 100vh;
  background-color: #f1f5f9;
  padding: 24px 32px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #0f172a;
  box-sizing: border-box;
}

/* Header */
.sci-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  color: #334155;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.sci-btn-back:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-badge-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #0284c7;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.sci-view-title {
  font-size: 1.65rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1.2;
}

.sci-view-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 4px 0 0 0;
}

.sci-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.sci-live-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  padding: 8px 14px;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #166534;
}

.live-pulse-dot {
  width: 10px;
  height: 10px;
  background-color: #22c55e;
  border-radius: 50%;
  box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
  animation: pulse-green 1.5s infinite;
}

@keyframes pulse-green {
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 8px rgba(34, 197, 94, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
}

/* Buttons */
.sci-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  justify-content: center;
}

.sci-btn-primary {
  background: #0284c7;
  color: #ffffff;
}

.sci-btn-primary:hover {
  background: #0369a1;
}

.sci-btn-secondary {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.sci-btn-secondary:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.sci-btn-danger-outline {
  background: #ffffff;
  border: 1px solid #fecaca;
  color: #dc2626;
  min-height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.sci-btn-danger-outline:hover {
  background: #fee2e2;
}

/* Bento KPI Grid */
.sci-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.sci-kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.sci-kpi-card.primary {
  border-left: 4px solid #10b981;
}

.sci-kpi-card.alert {
  border-left: 4px solid #ef4444;
}

.kpi-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  flex-shrink: 0;
}

.kpi-icon-wrap.red {
  background: #fee2e2;
  color: #dc2626;
}

.kpi-icon-wrap.green {
  background: #f0fdf4;
  color: #16a34a;
}

.kpi-icon-wrap.blue {
  background: #eff6ff;
  color: #2563eb;
}

.kpi-meta {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
}

.kpi-value {
  font-size: 1.6rem;
  font-weight: 800;
  color: #0f172a;
  margin: 2px 0;
}

.kpi-value.text-normal {
  font-size: 1.15rem;
  font-weight: 700;
}

.kpi-sub {
  font-size: 0.75rem;
  color: #94a3b8;
}

.text-danger {
  color: #dc2626 !important;
}

/* Calls Card & Grid */
.sci-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
  overflow: hidden;
}

.calls-card-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8fafc;
  flex-wrap: wrap;
  gap: 12px;
}

.calls-card-header h3 {
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
}

.header-hint {
  font-size: 0.85rem;
  color: #64748b;
}

.calls-grid-wrapper {
  padding: 24px;
  min-height: 380px;
}

.calls-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
}

/* Call Tile */
.call-tile {
  background: #ffffff;
  border: 2px solid #ef4444;
  border-radius: 18px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(239, 68, 68, 0.15);
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  overflow: hidden;
}

.call-tile:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 14px 28px rgba(239, 68, 68, 0.25);
  border-color: #dc2626;
}

.call-tile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.sala-pill {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  background: #fee2e2;
  color: #991b1b;
  padding: 4px 10px;
  border-radius: 6px;
  letter-spacing: 0.5px;
}

.pulse-beacon {
  width: 12px;
  height: 12px;
  background: #ef4444;
  border-radius: 50%;
  animation: pulse-beacon 1.2s infinite;
}

@keyframes pulse-beacon {
  0% { transform: scale(0.9); opacity: 1; }
  50% { transform: scale(1.4); opacity: 0.4; }
  100% { transform: scale(0.9); opacity: 1; }
}

.call-tile-body {
  padding: 16px 0;
  text-align: center;
}

.table-avatar {
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  background: #fef2f2;
  padding: 12px 24px;
  border-radius: 16px;
}

.t-prefix {
  font-size: 1.5rem;
  font-weight: 700;
  color: #991b1b;
  margin-right: 2px;
}

.t-number {
  font-size: 2.8rem;
  font-weight: 900;
  color: #dc2626;
  line-height: 1;
}

.call-tile-footer {
  margin-top: 12px;
}

.sci-btn-evadi {
  width: 100%;
  min-height: 42px;
  background: #ef4444;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.call-tile:hover .sci-btn-evadi {
  background: #dc2626;
}

/* Animations */
.call-card-enter-active,
.call-card-leave-active {
  transition: all 0.3s ease;
}

.call-card-enter-from {
  opacity: 0;
  transform: scale(0.8) translateY(-20px);
}

.call-card-leave-to {
  opacity: 0;
  transform: scale(0.8) translateY(20px);
}

/* Modal Dialog */
.sci-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 20px;
  box-sizing: border-box;
}

.sci-modal-dialog.mini {
  background: #ffffff;
  border-radius: 20px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.sci-modal-header {
  padding: 18px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-title-wrap h3 {
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
  margin: 2px 0 0 0;
}

.sci-modal-close {
  background: transparent;
  border: none;
  font-size: 1.8rem;
  color: #64748b;
  cursor: pointer;
}

.sci-modal-body {
  padding: 24px;
}

.evade-preview {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.preview-badge {
  font-size: 1.6rem;
  font-weight: 900;
  color: #dc2626;
  background: #fee2e2;
  padding: 6px 18px;
  border-radius: 12px;
}

.preview-sala {
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748b;
}

.evade-question {
  font-size: 0.95rem;
  color: #334155;
  margin-bottom: 24px;
  line-height: 1.5;
}

.modal-actions-row {
  display: flex;
  gap: 12px;
}

.flex-1 {
  flex: 1;
}

/* Feedback States */
.sci-state-feedback {
  padding: 60px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.empty-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin-bottom: 16px;
}

.empty-icon-wrap.green {
  background: #f0fdf4;
  color: #16a34a;
}

.state-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.state-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0;
  max-width: 440px;
}
</style>
